// catalogSlice.ts

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import api from '../../services/api';
import { Category } from '../../types/api';

// Re-export types for components
export type { Category };

interface CatalogState {
  categories: Category[];
  loading: boolean;
  error: string | null;
}

const initialState: CatalogState = {
  categories: [],
  loading: false,
  error: null,
};

export const fetchCategories = createAsyncThunk<Category[], void, { state: { catalog: CatalogState }; rejectValue: string }>(
  'catalog/fetchCategories',
  async (_, thunkAPI) => {
    try {
      const response = await api.get<Category[]>('/api/categories/');
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      const message = axiosError.response?.data?.error || axiosError.message || 'Failed to fetch categories';
      return thunkAPI.rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { catalog } = getState();
      return !catalog.loading && catalog.categories.length === 0;
    },
  }
);

const catalogSlice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Unknown error';
      });
  },
});

export default catalogSlice.reducer;
